import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/i18n";
import { scrollToId } from "@/lib/scroll";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const solid = scrolled || location.pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["services", t.nav.services],
    ["manifesto", t.nav.dna],
    ["avis", t.nav.reviews],
    ["faq", t.nav.faq],
    ["contact", t.nav.contact],
    ["blog", t.nav.blog],
  ];

  const go = (id) => {
    setOpen(false);
    if (id === "blog") {
      navigate("/blog");
      window.scrollTo(0, 0);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
      return;
    }
    scrollToId(id);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid ? "bg-[#FAF8F5]/85 backdrop-blur-xl border-b border-[#0E1B2A]/10 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={`font-display text-lg sm:text-xl tracking-[0.18em] font-semibold transition-colors duration-500 ${solid ? "text-[#0E1B2A]" : "text-[#FAF8F5]"}`}
          >
            SERVICE<span className="text-[#C87A57]">+</span>
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map(([id, label]) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className={`text-sm font-medium tracking-wide transition-colors duration-500 hover:text-[#C87A57] ${solid ? "text-[#0E1B2A]/80" : "text-[#FAF8F5]/85"}`}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div
              className={`relative flex rounded-full border p-0.5 ${solid ? "border-[#0E1B2A]/15" : "border-white/30"}`}
              role="group"
              aria-label="Language"
            >
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`language-toggle-${l}`}
                  onClick={() => setLang(l)}
                  className={`relative z-10 px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                    lang === l ? (solid ? "text-[#FAF8F5]" : "text-[#0E1B2A]") : solid ? "text-[#0E1B2A]/60" : "text-white/70"
                  }`}
                >
                  {lang === l && (
                    <motion.span
                      layoutId="lang-pill"
                      className={`absolute inset-0 -z-10 rounded-full ${solid ? "bg-[#0E1B2A]" : "bg-[#FAF8F5]"}`}
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                  {l}
                </button>
              ))}
            </div>

            <a
              data-testid="nav-phone-link"
              href="tel:+33767473293"
              className={`hidden md:flex items-center gap-2 text-sm font-semibold transition-colors duration-500 ${solid ? "text-[#0E1B2A]" : "text-[#FAF8F5]"}`}
            >
              <Phone size={15} className="text-[#C87A57]" />
              07 67 47 32 93
            </a>

            <button
              data-testid="nav-quote-btn"
              onClick={() => go("contact")}
              className="hidden sm:inline-flex items-center rounded-full bg-[#C87A57] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#b06a48] hover:shadow-lg hover:-translate-y-0.5"
            >
              {t.nav.quote}
            </button>

            <button
              data-testid="nav-mobile-menu-btn"
              onClick={() => setOpen(!open)}
              className={`lg:hidden p-2 transition-colors duration-500 ${solid ? "text-[#0E1B2A]" : "text-[#FAF8F5]"}`}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-[#FAF8F5]/95 backdrop-blur-xl border-b border-[#0E1B2A]/10 px-6 pb-6 pt-2"
        >
          {links.map(([id, label]) => (
            <button
              key={id}
              data-testid={`nav-mobile-link-${id}`}
              onClick={() => go(id)}
              className="block w-full text-left py-3 text-base font-medium text-[#0E1B2A] border-b border-[#0E1B2A]/5"
            >
              {label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-quote-btn"
            onClick={() => go("contact")}
            className="mt-4 w-full rounded-full bg-[#C87A57] py-3 text-sm font-semibold text-white"
          >
            {t.nav.quote}
          </button>
        </motion.div>
      )}
    </header>
  );
}
